/**
 * PostHog autocapture hygiene — capture-time `before_send` processing for
 * `$autocapture` events on the onboarding surface.
 *
 * WHY this exists: posthog-js autocaptures a click on ANY element whose
 * computed `cursor` is `pointer` (autocapture-utils.ts `shouldCaptureDomEvent`),
 * and `cursor` inherits. In a Tailwind UI, every presentation element nested
 * inside a `cursor-pointer` button therefore becomes an autocapture target, so
 * the captured element is frequently a layout `span`/`svg`/`div` whose only
 * recorded identity is a utility-class blob. Those rows say "someone clicked
 * inside something" and nothing more, while the meaningful interaction is
 * already covered by a named canonical event. This filter drops that class of
 * event on the onboarding journey and leaves every identifiable click, every
 * other event type, and every other surface untouched.
 *
 * Per rule: code-comments-required (ai.knowledge/global)
 * Per rule: reference-rules-in-comments (ai.knowledge/global)
 */

/**
 * Element tags posthog-js itself treats as inherently interactive
 * (`autocaptureCompatibleElements`). A click captured directly ON one of these
 * always identifies a real control, so it is never noise.
 */
export const AUTOCAPTURE_INTERACTIVE_TAGS = [
  'a',
  'button',
  'form',
  'input',
  'select',
  'textarea',
  'label'
] as const;

export type AutocaptureInteractiveTag = (typeof AUTOCAPTURE_INTERACTIVE_TAGS)[number];

const INTERACTIVE_TAG_SET: ReadonlySet<string> = new Set(AUTOCAPTURE_INTERACTIVE_TAGS);

/**
 * Attributes that give a captured element a durable analytic identity: an
 * analyst can tell WHICH control was clicked from any one of them. Deliberately
 * excludes presentation attributes (`class`, `style`, `fill`, `viewBox`,
 * `aria-hidden`) — an element carrying only those is indistinguishable from any
 * other box on the screen.
 */
export const AUTOCAPTURE_IDENTITY_ATTRIBUTES = [
  'href',
  'download',
  'id',
  'name',
  'type',
  'role',
  'title',
  'alt',
  'placeholder',
  'value',
  'aria-label',
  'data-stag',
  'data-testid'
] as const;

export type AutocaptureIdentityAttribute = (typeof AUTOCAPTURE_IDENTITY_ATTRIBUTES)[number];

/**
 * Onboarding paths matched in full.
 *
 * `/select-studio` is the post-login studio picker. It is listed deliberately
 * even though this build's route table does not mount it: the picker shipped,
 * was reverted from `main`, and is still served by other deploys, so the entry
 * is inert here and begins suppressing the moment the route lands again. Every
 * other entry MUST correspond to a path the router actually serves — an entry
 * that can never match silently claims coverage the surface does not have,
 * which is why this list is pinned against the route table in
 * `__tests__/autocapture-hygiene.test.ts` rather than against itself.
 */
export const ONBOARDING_SURFACE_EXACT_PATHS = {
  LOGIN: '/login',
  REGISTER: '/register',
  SELECT_STUDIO: '/select-studio'
} as const;

/**
 * Onboarding paths matched on whole leading segments, children included.
 *
 * The auto-link verification screen is deliberately NOT here. Its code lives
 * under `routes/verify-auto-link/`, but that is a source directory and not a
 * route: `<AutoLinkGuard>` is mounted on the index route `/`, which is also the
 * Studio boot handoff and, in the browser build, the web entry point. Listing
 * either name would reach beyond onboarding or match nothing at all.
 */
export const ONBOARDING_SURFACE_PATH_PREFIXES = {
  AGENT_SETUP: '/agent/setup',
  INVITE: '/invite'
} as const;

/**
 * The bridge-linking wizard nests an id between its fixed segments
 * (`/agent/:agentId/link/...`), so it cannot be expressed as a literal prefix.
 */
export const ONBOARDING_SURFACE_AGENT_LINK_SEGMENTS = {
  HEAD: 'agent',
  STEP: 'link'
} as const;

/** Named parts of a hygiene rule id, so the composed id cannot be mistyped. */
export const AUTOCAPTURE_HYGIENE_RULE_ROOTS = {
  NO_IDENTITY_TARGET: 'no_identity_target'
} as const;

export const AUTOCAPTURE_HYGIENE_DECISIONS = {
  PASS: 'pass',
  SUPPRESS: 'suppress'
} as const;

type AutocaptureHygieneRuleRoot =
  (typeof AUTOCAPTURE_HYGIENE_RULE_ROOTS)[keyof typeof AUTOCAPTURE_HYGIENE_RULE_ROOTS];

export type AutocaptureHygieneDecision =
  (typeof AUTOCAPTURE_HYGIENE_DECISIONS)[keyof typeof AUTOCAPTURE_HYGIENE_DECISIONS];

/**
 * Rule ids are composed from the typed parts above rather than written as
 * literals, so an unknown root or a root paired with an unknown decision fails
 * type-checking instead of shipping as a silent diagnostic string.
 */
export type AutocaptureHygieneRuleId =
  `${AutocaptureHygieneRuleRoot}:${AutocaptureHygieneDecision}`;

const composeRuleId = (
  root: AutocaptureHygieneRuleRoot,
  decision: AutocaptureHygieneDecision
): AutocaptureHygieneRuleId => `${root}:${decision}`;

/** PostHog property names this filter reads off an `$autocapture` envelope. */
export const AUTOCAPTURE_PROPERTY_NAMES = {
  CURRENT_URL: '$current_url',
  ELEMENTS: '$elements',
  ELEMENTS_CHAIN: '$elements_chain',
  EL_TEXT: '$el_text',
  EVENT_TYPE: '$event_type'
} as const;

/**
 * `$event_type` values posthog-js puts on an autocapture envelope. It stamps the
 * DOM event type unconditionally (`getDefaultProperties`), so the property is
 * always present and can be relied on to narrow the decision. These are DOM
 * event names, so they stay literals rather than becoming Iris-owned constants —
 * they are shared here only so the filter and its tests cannot disagree.
 */
export const AUTOCAPTURE_EVENT_TYPES = {
  CLICK: 'click',
  CHANGE: 'change',
  SUBMIT: 'submit'
} as const;

/** The subset of an `$autocapture` envelope the decision depends on. */
export interface AutocaptureProperties {
  [key: string]: unknown;
  $current_url?: unknown;
  $el_text?: unknown;
  $elements?: unknown;
  $elements_chain?: unknown;
  $event_type?: unknown;
}

export interface AutocaptureHygieneOptions {
  /**
   * Keep noise flowing while still reporting the decision, so an audit can
   * measure what the filter would drop without a deploy. Mirrors the verbose
   * escape hatch on the exception noise filter.
   */
  verbose?: boolean;
}

export interface AutocaptureHygieneResult {
  decision: AutocaptureHygieneDecision;
  /** Present only when a rule matched, whether or not verbose kept the event. */
  ruleId?: AutocaptureHygieneRuleId;
}

/** The identifying facts about the element a click was captured on. */
export interface CapturedTarget {
  tagName: string;
  text: string;
  identityAttributes: readonly string[];
}

const PASS: AutocaptureHygieneResult = { decision: AUTOCAPTURE_HYGIENE_DECISIONS.PASS };

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/** Narrow without a type assertion so a hostile envelope stays type-safe. */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Path of a captured URL. Autocapture carries an absolute `$current_url`, but
 * the renderer's Electron origin is an ephemeral localhost port, so parsing has
 * to tolerate any origin — and a relative value, which some analytics paths set
 * directly.
 */
export function pathnameOf(currentUrl: unknown): string | undefined {
  if (!isNonEmptyString(currentUrl)) {
    return undefined;
  }
  try {
    // A base is required for relative inputs and is ignored for absolute ones.
    return new URL(currentUrl, 'http://localhost').pathname;
  } catch {
    return undefined;
  }
}

/** Split a path into its non-empty segments (`/a/b/` → `['a', 'b']`). */
const segmentsOf = (pathname: string): string[] => pathname.split('/').filter((s) => s.length > 0);

/**
 * True when the path belongs to the desktop onboarding journey. Prefixes match
 * on whole segments so `/agent/setup-diagnostics` can never be mistaken for a
 * child of `/agent/setup`.
 */
export function isOnboardingSurfacePath(pathname: string | undefined): boolean {
  if (pathname === undefined || pathname.length === 0) {
    return false;
  }
  const normalized =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  for (const exact of Object.values(ONBOARDING_SURFACE_EXACT_PATHS)) {
    if (normalized === exact) {
      return true;
    }
  }

  for (const prefix of Object.values(ONBOARDING_SURFACE_PATH_PREFIXES)) {
    if (normalized === prefix || normalized.startsWith(`${prefix}/`)) {
      return true;
    }
  }

  const segments = segmentsOf(normalized);
  return (
    segments[0] === ONBOARDING_SURFACE_AGENT_LINK_SEGMENTS.HEAD &&
    segments[2] === ONBOARDING_SURFACE_AGENT_LINK_SEGMENTS.STEP
  );
}

/**
 * Read one element out of the `$elements` array form. posthog-js emits this
 * shape only while the server has not asked for the chain string, so both forms
 * have to be understood (autocapture.ts `_elementsChainAsString`, which is set
 * from the `/flags` response).
 *
 * `tag_name` is written for every element by `getPropertiesFromElement`, so its
 * absence means this is not a shape the parser understands — and an element the
 * parser did not understand must not be classified. See the fail-open note on
 * `filterAutocapture`.
 */
function targetFromElementRecord(element: unknown): CapturedTarget | undefined {
  if (!isRecord(element) || !isNonEmptyString(element.tag_name)) {
    return undefined;
  }
  const rawText = element[AUTOCAPTURE_PROPERTY_NAMES.EL_TEXT];
  const identityAttributes = AUTOCAPTURE_IDENTITY_ATTRIBUTES.filter((attribute) =>
    isNonEmptyString(element[`attr__${attribute}`])
  );
  return {
    tagName: element.tag_name.toLowerCase(),
    text: isNonEmptyString(rawText) ? rawText.trim() : '',
    identityAttributes
  };
}

/**
 * Split an `$elements_chain` into its per-element segments, clicked element
 * first and its ancestors after it. Segments are joined with `;`, and posthog-js
 * escapes only quotes when serializing attribute values — a `style="…;…"` value
 * therefore contains a raw `;`. Scanning with quote awareness instead of
 * splitting keeps such an element parseable.
 */
export function chainSegments(chain: string): string[] {
  const segments: string[] = [];
  let start = 0;
  let inQuotes = false;
  for (let i = 0; i < chain.length; i++) {
    const char = chain[i];
    if (char === '\\') {
      i++;
      continue;
    }
    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (char === ';' && !inQuotes) {
      segments.push(chain.slice(start, i));
      start = i + 1;
    }
  }
  segments.push(chain.slice(start));
  return segments;
}

/** The chain's first segment: the element the event was captured on. */
export function headSegmentOfChain(chain: string): string {
  const [head] = chainSegments(chain);
  return head ?? '';
}

/** Leading tag of a chain segment, before its class list or attribute block. */
const TAG_PATTERN = /^[A-Za-z][A-Za-z0-9-]*/;

/**
 * Parse the `key="value"` pairs of one chain segment.
 *
 * The serializer concatenates pairs with no separator
 * (`text="x"nth-child="1"`), and a Tailwind class list routinely contains both
 * `:` and `.` (`active:translate-y-0`, `gap-[5px]`), so the block cannot be
 * found by splitting on the first colon. Instead each quoted value is located
 * first; its key is then whatever precedes the `=`, taken after the last `:`
 * for the opening pair (which still carries the tag and class list). Because
 * the scan jumps past every quoted value, a `:` or `;` INSIDE a value (a
 * `style` attribute, for instance) can never be mistaken for a delimiter.
 */
function parseChainSegmentAttributes(segment: string): Map<string, string> {
  const attributes = new Map<string, string>();
  let cursor = 0;
  let isFirstPair = true;

  for (let i = 0; i < segment.length; i++) {
    if (segment[i] !== '=' || segment[i + 1] !== '"') {
      continue;
    }
    const keySource = segment.slice(cursor, i);
    // The opening pair's slice still holds `tag.class-list:key`.
    const key = isFirstPair ? keySource.slice(keySource.lastIndexOf(':') + 1) : keySource;

    // Walk the quoted value, honoring the serializer's backslash-escaped quotes.
    let end = i + 2;
    let value = '';
    while (end < segment.length) {
      const char = segment[end];
      if (char === '\\' && end + 1 < segment.length) {
        value += segment[end + 1];
        end += 2;
        continue;
      }
      if (char === '"') {
        break;
      }
      value += char;
      end++;
    }

    if (key.length > 0) {
      attributes.set(key, value);
    }
    cursor = end + 1;
    i = end;
    isFirstPair = false;
  }

  return attributes;
}

/**
 * Read one element out of the `$elements_chain` string form. A segment is
 * `tag.class1.class2:key="value"key2="value2"`, where `$el_text` appears as
 * `text` and `attr__id` as `attr_id` (autocapture-utils.ts `extractElements`
 * renames both); every other attribute keeps its `attr__` prefix.
 *
 * A tag with no attribute block is rejected rather than read as a bare tag.
 * `elementsToString` always emits `:` plus at least `nth-child` and
 * `nth-of-type` (both defaulted), so a real segment always has attributes —
 * whereas an opaque token from some future serializer would otherwise satisfy
 * the tag pattern on its own and be classified as an anonymous element. Failing
 * open here is what makes the whole filter fail open on a shape it does not
 * recognise.
 */
function targetFromChainSegment(segment: string): CapturedTarget | undefined {
  const tagName = (TAG_PATTERN.exec(segment)?.[0] ?? '').toLowerCase();
  const attributes = parseChainSegmentAttributes(segment);
  if (tagName.length === 0 || attributes.size === 0) {
    return undefined;
  }

  const text = attributes.get('text') ?? '';
  const identityAttributes = AUTOCAPTURE_IDENTITY_ATTRIBUTES.filter((attribute) => {
    // `href` and `id` are renamed by the chain serializer; the rest keep attr__.
    const candidates =
      attribute === 'href'
        ? ['href', 'attr__href']
        : attribute === 'id'
          ? ['attr_id', 'attr__id']
          : [`attr__${attribute}`];
    return candidates.some((key) => isNonEmptyString(attributes.get(key)));
  });

  return { tagName, text: text.trim(), identityAttributes };
}

/**
 * Read a whole element list, clicked element first. Parsing stops at the first
 * element the parser cannot read: if that is the clicked element there is
 * nothing to classify, and above it the ancestry is unknown, so guessing further
 * would risk both a wrong suppression and a wrong keep.
 */
function chainFrom<T>(
  items: readonly T[],
  read: (item: T) => CapturedTarget | undefined
): readonly CapturedTarget[] | undefined {
  const targets: CapturedTarget[] = [];
  for (const item of items) {
    const target = read(item);
    if (target === undefined) {
      break;
    }
    targets.push(target);
  }
  return targets.length === 0 ? undefined : targets;
}

const targetsFromElementsArray = (elements: unknown): readonly CapturedTarget[] | undefined =>
  Array.isArray(elements) ? chainFrom(elements, targetFromElementRecord) : undefined;

const targetsFromElementsChain = (chain: unknown): readonly CapturedTarget[] | undefined =>
  isNonEmptyString(chain) ? chainFrom(chainSegments(chain), targetFromChainSegment) : undefined;

/**
 * Resolve the clicked element and its ancestors from whichever shape the SDK
 * emitted. The array form is preferred because it is unambiguous; the top-level
 * `$el_text` is a copy of the clicked element's text and backfills it when the
 * chain string omitted an empty value.
 */
export function resolveCapturedChain(properties: AutocaptureProperties): readonly CapturedTarget[] {
  const chain =
    targetsFromElementsArray(properties[AUTOCAPTURE_PROPERTY_NAMES.ELEMENTS]) ??
    targetsFromElementsChain(properties[AUTOCAPTURE_PROPERTY_NAMES.ELEMENTS_CHAIN]);

  const [target, ...ancestors] = chain ?? [];
  if (target === undefined) {
    return [];
  }
  if (
    target.text.length === 0 &&
    isNonEmptyString(properties[AUTOCAPTURE_PROPERTY_NAMES.EL_TEXT])
  ) {
    return [
      { ...target, text: String(properties[AUTOCAPTURE_PROPERTY_NAMES.EL_TEXT]).trim() },
      ...ancestors
    ];
  }
  return [target, ...ancestors];
}

/** The element the event was captured on, if it could be read at all. */
export function resolveCapturedTarget(
  properties: AutocaptureProperties
): CapturedTarget | undefined {
  return resolveCapturedChain(properties)[0];
}

/** An element an analyst can name from what was recorded about it. */
const carriesIdentity = (target: CapturedTarget): boolean =>
  target.text.length > 0 || target.identityAttributes.length > 0;

/**
 * A captured click is noise when the element it landed on is neither an
 * interactive control nor carries any identifying attribute or text. Such a row
 * cannot be attributed to a control by any consumer, so it only dilutes the
 * surface it was captured on.
 */
function isNoIdentityTarget(target: CapturedTarget): boolean {
  if (INTERACTIVE_TAG_SET.has(target.tagName)) {
    return false;
  }
  return !carriesIdentity(target);
}

/** `role` marks a control that is not built from an interactive tag. */
const ROLE_ATTRIBUTE: AutocaptureIdentityAttribute = 'role';

/**
 * The only ancestors allowed to attribute a click: the clicked element's
 * IMMEDIATE parent, and the nearest enclosing control (an interactive tag, or
 * anything declaring an ARIA `role`).
 *
 * Bounded deliberately. The whole app mounts under `<div id="root">`, so a rule
 * that accepted an identifying attribute anywhere up the tree would find one
 * above every click on every screen and could never suppress anything. The
 * immediate parent is in because PostHog Actions are written directly against
 * that relationship — a live one targets `tag_name: path` through the selector
 * `[id="radix-\:r1k\:"] > path`.
 */
function attributingAncestors(ancestors: readonly CapturedTarget[]): readonly CapturedTarget[] {
  const [parent] = ancestors;
  if (parent === undefined) {
    return [];
  }
  const control = ancestors.find(
    (ancestor) =>
      INTERACTIVE_TAG_SET.has(ancestor.tagName) ||
      ancestor.identityAttributes.includes(ROLE_ATTRIBUTE)
  );
  return control === undefined || control === parent ? [parent] : [parent, control];
}

/**
 * Decide what to do with one `$autocapture` envelope.
 *
 * Fail-open by construction: anything this filter cannot fully understand — an
 * unparseable URL, a missing or unrecognised element chain, an off-surface path,
 * an event type other than a click — passes through. A hygiene filter must never
 * be able to silently delete telemetry it did not positively identify as noise.
 */
export function filterAutocapture(
  properties: AutocaptureProperties,
  options: AutocaptureHygieneOptions = {}
): AutocaptureHygieneResult {
  const pathname = pathnameOf(properties[AUTOCAPTURE_PROPERTY_NAMES.CURRENT_URL]);
  if (!isOnboardingSurfacePath(pathname)) {
    return PASS;
  }

  // Only a click can be the "clicked a box" row this rule exists to remove. The
  // SDK also autocaptures `change` and `submit`, which are form signal, so the
  // decision reads the event type rather than relying on the tag allowlist to
  // rule them out indirectly.
  if (properties[AUTOCAPTURE_PROPERTY_NAMES.EVENT_TYPE] !== AUTOCAPTURE_EVENT_TYPES.CLICK) {
    return PASS;
  }

  const [target, ...ancestors] = resolveCapturedChain(properties);
  if (target === undefined || !isNoIdentityTarget(target)) {
    return PASS;
  }

  // A decorative child of a NAMED element is still attributable: the chain
  // records the name, and PostHog Actions do target exactly that shape. Deleting
  // those rows would destroy real interactions unrecoverably — suppression
  // happens at capture time, and the verbose flag only restores events captured
  // after it is flipped. Only a click that is anonymous AND sits under anonymous
  // attributing ancestors is noise.
  if (attributingAncestors(ancestors).some(carriesIdentity)) {
    return PASS;
  }

  // Verbose keeps the event but still reports the match, so an audit can count
  // what suppression would remove without changing what ships.
  if (options.verbose === true) {
    return {
      decision: AUTOCAPTURE_HYGIENE_DECISIONS.PASS,
      ruleId: composeRuleId(
        AUTOCAPTURE_HYGIENE_RULE_ROOTS.NO_IDENTITY_TARGET,
        AUTOCAPTURE_HYGIENE_DECISIONS.PASS
      )
    };
  }

  return {
    decision: AUTOCAPTURE_HYGIENE_DECISIONS.SUPPRESS,
    ruleId: composeRuleId(
      AUTOCAPTURE_HYGIENE_RULE_ROOTS.NO_IDENTITY_TARGET,
      AUTOCAPTURE_HYGIENE_DECISIONS.SUPPRESS
    )
  };
}
